import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ExportFormat } from "@asd/customer-portal-ui";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import type {
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridSortModel,
} from "@mui/x-data-grid/models";

import { ProviderRegistrationForDisplay } from "~/utils/providerRegistrationHelpers";
import { useTableExport } from "~/utils/tableExport";
import { FIND_PROVIDERS_TEXT, NO_PROVIDERS_TEXT } from "../CareProviders";
import ContactProvider from "../ContactProvider";
import { TableViewState } from "../useCareProvidersState";
import TableDetailRow from "./TableDetailRow";
import { applyFilterItem, BASE_COLUMNS, buildProvidersCsv, extractSortValue, flattenForSearch } from "./tableHelpers";
import { TableViewToolbar, TableViewToolbarContext, TableViewToolbarContextValue } from "./TableViewToolbar";

interface TableViewProps {
  providers: ProviderRegistrationForDisplay[];
  hasCurrentSearch: boolean;
  tableViewState: TableViewState;
  setTableViewState: (patch: Partial<TableViewState>) => void;
}

const TableView: FC<TableViewProps> = ({ providers, hasCurrentSearch, tableViewState, setTableViewState }) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set(tableViewState.expandedIds));
  const [animatingIds, setAnimatingIds] = useState(new Set());
  const [columnWidths, setColumnWidths] = useState<any>(tableViewState.columnWidths);
  const [searchTerm, setSearchTerm] = useState(tableViewState.searchTerm);
  const [paginationModel, setPaginationModel] = useState(tableViewState.paginationModel);
  const [sortModel, setSortModel] = useState<GridSortModel>(tableViewState.sortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(tableViewState.filterModel);

  const clearSearchTermRef = useRef<() => void>(null);
  const exportTable = useTableExport();

  const toggleRow = (id: unknown) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setAnimatingIds((prevAnim) => new Set(prevAnim).add(id));
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleColumnResize = (params: { colDef: { field: any }; width: any }) => {
    setColumnWidths((prev: any) => ({ ...prev, [params.colDef.field]: params.width }));
  };

  const handleReset = useCallback(() => {
    clearSearchTermRef.current?.();
    setFilterModel({ items: [], logicOperator: "and" as GridLogicOperator });
    setSortModel([]);
    setPaginationModel({ page: 0, pageSize: 25 });
    setExpandedIds(new Set());
    setAnimatingIds(new Set());
  }, []);

  const gridKey = useMemo(
    () => JSON.stringify({ sort: sortModel, page: paginationModel.page, pageSize: paginationModel.pageSize }),
    [sortModel, paginationModel],
  );

  const columns: GridColDef<any>[] = useMemo(
    () => [
      {
        field: "expandIcon",
        headerName: "",
        width: 50,
        resizable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        colSpan: (_value: any, row: { isDetail: boolean }) => (row?.isDetail ? BASE_COLUMNS.length + 2 : 1),
        renderCell: (params: GridRenderCellParams) => {
          if (!params.row.isDetail) {
            const isExpanded = expandedIds.has(params.id);
            return (
              <IconButton
                size="small"
                sx={{
                  transition: "transform 0.3s ease",
                  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            );
          } else {
            return <TableDetailRow expandedIds={expandedIds} params={params} setAnimatingIds={setAnimatingIds} />;
          }
        },
      },
      ...BASE_COLUMNS.map((col) => ({
        ...col,
        width: columnWidths[col.field] ?? undefined,
        flex: columnWidths[col.field] ? undefined : (col.flex ?? 1),
      })),
      {
        field: "contact",
        headerName: "Contact",
        flex: 1,
        resizable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams) => !params.row.isDetail && <ContactProvider key="contact" {...params} />,
      },
    ],
    [expandedIds, columnWidths],
  );

  const filteredRows: ProviderRegistrationForDisplay[] = useMemo(() => {
    let result = [...providers];

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter((row) => flattenForSearch(row).includes(lowerSearch));
    }

    if (filterModel.items.length) {
      const { logicOperator } = filterModel;
      result = result.filter((row) => {
        const results = filterModel.items.map((item) => applyFilterItem(row, item));
        return logicOperator === "or" ? results.some(Boolean) : results.every(Boolean);
      });
    }

    return result;
  }, [searchTerm, providers, filterModel]);

  const sortedMasterRows: ProviderRegistrationForDisplay[] = useMemo(() => {
    if (!sortModel.length) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sortItem of sortModel) {
        const { field, sort } = sortItem;
        const isDesc = sort === "desc";

        const rawA = field in a ? a[field as keyof typeof a] : (a.registration as Record<string, any>)[field];
        const rawB = field in b ? b[field as keyof typeof b] : (b.registration as Record<string, any>)[field];

        const valA = extractSortValue(field, rawA);
        const valB = extractSortValue(field, rawB);

        let result = 0;
        if (typeof valA === "number" && typeof valB === "number") {
          result = valA - valB;
        } else {
          result = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: "base" });
        }

        if (result !== 0) return isDesc ? result * -1 : result;
      }
      return 0;
    });
  }, [filteredRows, sortModel]);

  const rowsWithDetails = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    const pagedMasterRows = sortedMasterRows.slice(start, end);

    return pagedMasterRows.flatMap((row) => [
      { ...row, isDetail: false, __groupOrder: 0 },
      {
        id: `${row.registration.providerRegistrationId}-detail`,
        master: { ...row },
        isDetail: true,
        __groupOrder: 1,
      },
    ]);
  }, [sortedMasterRows, paginationModel]);

  const handleExport = useCallback(
    (format: ExportFormat) => exportTable(buildProvidersCsv(sortedMasterRows), "providers", format),
    [exportTable, sortedMasterRows],
  );

  const toolbarContextValue = useMemo<TableViewToolbarContextValue>(
    () => ({
      searchTerm,
      filterModel,
      setFilterModel,
      setPaginationModel,
      setExpandedIds,
      setAnimatingIds,
      setSearchTerm,
      clearSearchTermRef,
      handleReset,
      handleExport,
    }),
    [searchTerm, filterModel, handleReset, handleExport],
  );

  useEffect(() => {
    setTableViewState({
      expandedIds: [...expandedIds],
      columnWidths,
      searchTerm,
      paginationModel,
      sortModel,
      filterModel,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds, columnWidths, searchTerm, paginationModel, sortModel, filterModel]);

  if (rowsWithDetails) {
    return (
      <TableViewToolbarContext.Provider value={toolbarContextValue}>
        <DataGrid
          key={gridKey}
          autosizeOptions={{ expand: true, includeHeaders: true, includeOutliers: true }}
          columns={columns}
          filterMode="server"
          getEstimatedRowHeight={() => 52}
          getRowClassName={(params: GridRowClassNameParams) => {
            let className = "";
            if (params.indexRelativeToCurrentPage % 2 === 0) {
              className += params.indexRelativeToCurrentPage % 4 === 0 ? "even" : "oddmaster";
            }
            if (!params.row.isDetail && params.row.distance > params.row.registration.travelDistance) {
              className += " outOfBounds";
            }
            return className.trim();
          }}
          getRowHeight={(params) => {
            if (params.model.isDetail) {
              const mId = params.model.master.registration.providerRegistrationId;
              return expandedIds.has(mId) || animatingIds.has(mId) ? "auto" : 1;
            }
          }}
          getRowId={(row) => (row.isDetail ? row.id : row.registration.providerRegistrationId)}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 }, rowCount: 0 },
          }}
          localeText={{ noRowsLabel: hasCurrentSearch ? NO_PROVIDERS_TEXT : FIND_PROVIDERS_TEXT }}
          onColumnWidthChange={handleColumnResize}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => !params.row.isDetail && toggleRow(params.id)}
          onSortModelChange={(newModel) => {
            setSortModel(newModel);
            setPaginationModel((prev: any) => ({ ...prev, page: 0 }));
            setExpandedIds(new Set());
            setAnimatingIds(new Set());
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={sortedMasterRows.length}
          rows={rowsWithDetails}
          slots={{ toolbar: TableViewToolbar }}
          sortingMode="server"
          sortModel={sortModel}
          sx={{
            "& .MuiDataGrid-row": {
              borderBottom: "none",
              transition: "background-color 0.2s",
              '&[style*="height: 0px"]': { display: "none" },
              "& .MuiDataGrid-row--dynamicHeight": { overflow: "visible !important" },
              "&.outOfBounds": {
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  right: 0,
                  height: "1px",
                  backgroundColor: (theme) => theme.palette.grey[600],
                  zIndex: 1,
                  pointerEvents: "none",
                },
                "& .MuiDataGrid-cell": { color: "text.disabled" },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none !important",
                [`&[data-field="expandIcon"]`]: { overflow: "initial", display: "flex", alignItems: "center", justifyContent: "center" },
                [`&[data-field="contact"]`]: { display: "flex", alignItems: "center", justifyContent: "center" },
              },
              "&:not(.MuiDataGrid-row--dynamicHeight)": { minHeight: "0px !important", maxHeight: "none !important" },
            },
          }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
        />
      </TableViewToolbarContext.Provider>
    );
  }
};

export default TableView;
